import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { UserRoleEnum } from '@/models/features/user.models'

import DropdownDots from '../../DropdownDots/DropdownDots'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import api from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

const ClientActionsMenu = ({
  newClientData,
  userRole,
  setFieldValue,
  handleUpdateClientActiveStatus,
  setSubmitting,
  handleDelete,
}) => {
  const { t } = useTranslation(['clients-legacy', 'common'])
  const { displaySnackbar } = useSnackbar()
  const [removePopupOpen, setRemovePopupOpen] = useState(false)
  const [deactivatePopupOpen, setDeactivatePopupOpen] = useState(false)

  const handleConfirmDelete = () => {
    handleDelete(false)
      .catch(() => displaySnackbar(t('clients-legacy:profileView.actionsMenu.removeError'), true))
      .finally(() => setRemovePopupOpen(false))
  }

  const handleConfirmDeactivate = () => {
    handleUpdateClientActiveStatus(false, setFieldValue, setSubmitting)
    setDeactivatePopupOpen(false)
  }

  const handleResendInvitationEmail = () => {
    api
      .resendActivationLink(newClientData.user?.email)
      .then(() =>
        displaySnackbar(t('clients-legacy:profileView.actionsMenu.resendInvitationSuccess')),
      )
      .catch(() => displaySnackbar(t('clients-legacy:newClientForm.serverError'), true))
  }

  // Note: FrontDesk Manager (ShiftManager) is not allowed to activate/deactivate residents.
  const canToggleActive = userRole !== UserRoleEnum.enum.ShiftManager

  const options = [
    {
      id: 0,
      text: t('clients-legacy:profileView.actionsMenu.resendInvitationEmail'),
      action: () => handleResendInvitationEmail(),
    },
    ...(canToggleActive
      ? [
          {
            id: 1,
            text: newClientData.active
              ? t('clients-legacy:profileView.actionsMenu.deactivateContact')
              : t('clients-legacy:profileView.actionsMenu.activateContact'),
            action: () =>
              newClientData.active
                ? setDeactivatePopupOpen(true)
                : handleUpdateClientActiveStatus(true, setFieldValue, setSubmitting),
          },
        ]
      : []),
    {
      id: 3,
      text: t('clients-legacy:profileView.actionsMenu.deleteContact'),
      optionClassName: 'delete-action',
      action: () => setRemovePopupOpen(true),
    },
  ]

  return (
    <React.Fragment>
      <DropdownDots options={options} />
      <ConfirmPopup
        title={t('common:confirmPopup.confirmation')}
        body={t('clients-legacy:profileView.actionsMenu.deleteConfirmBody', {
          firstName: newClientData.user?.firstName,
          lastName: newClientData.user?.lastName,
        })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('clients-legacy:profileView.actionsMenu.deleteConfirmText')}
        isOpen={removePopupOpen}
        cancelCallback={() => setRemovePopupOpen(false)}
        confirmCallback={handleConfirmDelete}
      />
      <ConfirmPopup
        title="Confirmation"
        body="Deactivating this resident will revoke keyless access for any of their guests. Do you want to proceed?"
        cancelText="Cancel"
        confirmText="Yes, deactivate"
        isOpen={deactivatePopupOpen}
        cancelCallback={() => setDeactivatePopupOpen(false)}
        confirmCallback={handleConfirmDeactivate}
      />
    </React.Fragment>
  )
}

ClientActionsMenu.propTypes = {
  newClientData: PropTypes.object,
  userRole: PropTypes.string,
  setFieldValue: PropTypes.func,
  handleUpdateClientActiveStatus: PropTypes.func,
  setSubmitting: PropTypes.func,
  handleDelete: PropTypes.func,
}

export default ClientActionsMenu
